import {
  type ChangeIssueStateInput,
  type CreateIssueInput,
  type CreateIssueTaskInput,
  type LinkIssueTaskInput,
  type PortalProject,
  type ReopenIssueInput,
  type IssueDetail,
  type IssueFlowReport,
  type IssueList,
  type IssueSlaTarget,
  type IssueState,
  type TriageIssueInput,
  type UpdateIssueInput,
  type UpdateSlaTargetsInput,
  type AddIssueWatcherInput,
  type InviteClientInput,
  type InviteClientResult,
  type IssueTimeline,
  type IssueWatcher,
  type IssueWatcherList,
  type MyIssueFacets,
  type MyIssueFacetsQuery,
  type MyIssuePage,
  type MyIssuesQuery,
  type ProjectClient,
  type ReportableProject,
  type CreateIssueCommentInput,
  type IssueAssigneeCandidateList,
  type IssueCommentList,
} from '@apm/contracts';
import {
  type ClientInviteApplyResult,
  type ClientInvitePreview,
  type ClientInviteState,
  type DeclineClientRequestInput,
  type MyClientRequest,
  type ProjectClientRequestItem,
  type CreateIssueTypeInput,
  type IssueTypeItem,
  type UpdateIssueTypeInput,
} from '@apm/contracts';
import { apiFetch } from '../../lib/http';

export interface IssueQuery {
  readonly state?: IssueState;
  readonly openOnly?: boolean;
}

function queryString(query: IssueQuery): string {
  const parts: string[] = [];
  if (query.state) parts.push(`state=${query.state}`);
  if (query.openOnly) parts.push('openOnly=1');
  return parts.length === 0 ? '' : `?${parts.join('&')}`;
}

/** Tham số của `GET /me/issues` — bỏ giá trị rỗng để URL gọn và cache khoá ổn định. */
function myIssuesQuery(query: Partial<MyIssuesQuery>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const text = params.toString();
  return text ? `?${text}` : '';
}

export const issueApi = {
  // ── Người gửi issue — APM-ISX-d/-e/-f ─────────────────────────────────────
  myIssues: (query: Partial<MyIssuesQuery>) =>
    apiFetch<MyIssuePage>(`/me/issues${myIssuesQuery(query)}`),
  reportableProjects: () => apiFetch<{ items: ReportableProject[] }>('/me/issues/projects'),
  myIssueFacets: (query: Partial<MyIssueFacetsQuery>) =>
    apiFetch<MyIssueFacets>(`/me/issues/facets${myIssuesQuery(query)}`),
  timeline: (issueId: string) => apiFetch<IssueTimeline>(`/issues/${issueId}/timeline`),
  watchers: (issueId: string) => apiFetch<IssueWatcherList>(`/issues/${issueId}/watchers`),
  watcherCandidates: (issueId: string) =>
    apiFetch<{ items: IssueWatcher[] }>(`/issues/${issueId}/watcher-candidates`),
  addWatcher: (issueId: string, input: AddIssueWatcherInput) =>
    apiFetch<IssueWatcherList>(`/issues/${issueId}/watchers`, { method: 'POST', body: input }),
  removeWatcher: (issueId: string, userId: string) =>
    apiFetch<IssueWatcherList>(`/issues/${issueId}/watchers/${userId}`, { method: 'DELETE' }),
  clients: (projectId: string) => apiFetch<ProjectClient[]>(`/projects/${projectId}/clients`),
  inviteClient: (projectId: string, input: InviteClientInput) =>
    apiFetch<InviteClientResult>(`/projects/${projectId}/clients`, { method: 'POST', body: input }),

  // Link mời khách + đơn chờ duyệt — APM-CLINV-c.
  clientInvite: (projectId: string) =>
    apiFetch<ClientInviteState>(`/projects/${projectId}/client-invite`),
  createClientInvite: (projectId: string) =>
    apiFetch<ClientInviteState>(`/projects/${projectId}/client-invite`, { method: 'POST' }),
  revokeClientInvite: (projectId: string) =>
    apiFetch<void>(`/projects/${projectId}/client-invite`, { method: 'DELETE' }),
  clientRequests: (projectId: string) =>
    apiFetch<ProjectClientRequestItem[]>(`/projects/${projectId}/client-requests`),
  acceptClientRequest: (projectId: string, requestId: string) =>
    apiFetch<void>(`/projects/${projectId}/client-requests/${requestId}/accept`, {
      method: 'POST',
    }),
  declineClientRequest: (projectId: string, requestId: string, input: DeclineClientRequestInput) =>
    apiFetch<void>(`/projects/${projectId}/client-requests/${requestId}/decline`, {
      method: 'POST',
      body: input,
    }),
  /** Token đi trong body, không trên URL của API — access log của proxy. */
  previewClientInvite: (token: string) =>
    apiFetch<ClientInvitePreview>('/client-invites/preview', { method: 'POST', body: { token } }),
  applyClientInvite: (token: string) =>
    apiFetch<ClientInviteApplyResult>('/client-invites/apply', {
      method: 'POST',
      body: { token },
    }),
  myClientRequests: () => apiFetch<MyClientRequest[]>('/me/client-requests'),

  // Loại issue do dự án tự quản — APM-ITYPE-b.
  issueTypes: (projectId: string) =>
    apiFetch<IssueTypeItem[]>(`/projects/${projectId}/issue-types`),
  createIssueType: (projectId: string, input: CreateIssueTypeInput) =>
    apiFetch<IssueTypeItem>(`/projects/${projectId}/issue-types`, { method: 'POST', body: input }),
  updateIssueType: (projectId: string, typeId: string, input: UpdateIssueTypeInput) =>
    apiFetch<IssueTypeItem>(`/projects/${projectId}/issue-types/${typeId}`, {
      method: 'PATCH',
      body: input,
    }),
  /** Loại đang có issue ⇒ bắt buộc `replacementId` cùng cách xử lý (API trả 400 nếu thiếu). */
  deleteIssueType: (projectId: string, typeId: string, replacementId?: string) =>
    apiFetch<void>(
      `/projects/${projectId}/issue-types/${typeId}${
        replacementId ? `?replacementId=${replacementId}` : ''
      }`,
      { method: 'DELETE' },
    ),

  list: (projectId: string, query: IssueQuery = {}) =>
    apiFetch<IssueList>(`/projects/${projectId}/issues${queryString(query)}`),

  detail: (issueId: string) => apiFetch<IssueDetail>(`/issues/${issueId}`),

  // Trao đổi + giao người xử lý — APM-ISC-a/-b.
  comments: (issueId: string) => apiFetch<IssueCommentList>(`/issues/${issueId}/comments`),
  postComment: (issueId: string, input: CreateIssueCommentInput) =>
    apiFetch<IssueCommentList>(`/issues/${issueId}/comments`, { method: 'POST', body: input }),
  assigneeCandidates: (issueId: string) =>
    apiFetch<IssueAssigneeCandidateList>(`/issues/${issueId}/assignee-candidates`),

  /*
    ⚠️ Truyền **object**, không `JSON.stringify` — `apiFetch` tự serialize.
    Stringify ở đây là serialize hai lần: server nhận một chuỗi JSON, Zod trả
    400 cho mọi trường, và người dùng chỉ thấy "Dữ liệu chưa hợp lệ".
  */
  create: (projectId: string, input: CreateIssueInput) =>
    apiFetch<IssueDetail>(`/projects/${projectId}/issues`, { method: 'POST', body: input }),

  update: (issueId: string, input: UpdateIssueInput) =>
    apiFetch<IssueDetail>(`/issues/${issueId}`, { method: 'PATCH', body: input }),

  changeState: (issueId: string, input: ChangeIssueStateInput) =>
    apiFetch<IssueDetail>(`/issues/${issueId}/state`, { method: 'PATCH', body: input }),

  triage: (issueId: string, input: TriageIssueInput) =>
    apiFetch<IssueDetail>(`/issues/${issueId}/triage`, { method: 'PATCH', body: input }),

  remove: (issueId: string) => apiFetch<void>(`/issues/${issueId}`, { method: 'DELETE' }),

  /** Người báo xác nhận đã xong — APM-ISSUE-n. */
  confirm: (issueId: string) =>
    apiFetch<IssueDetail>(`/issues/${issueId}/confirm`, { method: 'POST', body: {} }),

  /** Người báo mở lại, kèm lý do bắt buộc. */
  reopen: (issueId: string, input: ReopenIssueInput) =>
    apiFetch<IssueDetail>(`/issues/${issueId}/reopen`, { method: 'POST', body: input }),

  /** Dự án mà người đang đăng nhập vào được — portal dùng để biết báo vào đâu. */
  myProjects: () => apiFetch<PortalProject[]>('/portal/projects'),

  createTask: (issueId: string, input: CreateIssueTaskInput) =>
    apiFetch<IssueDetail>(`/issues/${issueId}/tasks`, { method: 'POST', body: input }),

  linkTask: (issueId: string, input: LinkIssueTaskInput) =>
    apiFetch<IssueDetail>(`/issues/${issueId}/tasks/link`, { method: 'POST', body: input }),

  unlinkTask: (issueId: string, taskId: string) =>
    apiFetch<IssueDetail>(`/issues/${issueId}/tasks/${taskId}`, { method: 'DELETE' }),

  /**
   * Năm biểu đồ trong **một** lượt gọi — APM-ISSUE-j.
   *
   * ⚠️ Không truyền `asOf`: `from`/`to` *là* trục thời gian của nhóm này (luật
   * #7 ngoại lệ 2). Giao diện cũng không đặt `AsOfField` cạnh nó.
   */
  flow: (projectId: string, range: { from?: string; to?: string } = {}) => {
    const parts = [
      ...(range.from ? [`from=${range.from}`] : []),
      ...(range.to ? [`to=${range.to}`] : []),
    ];
    return apiFetch<IssueFlowReport>(
      `/projects/${projectId}/reports/issue-flow${parts.length ? `?${parts.join('&')}` : ''}`,
    );
  },

  slaTargets: (projectId: string) =>
    apiFetch<IssueSlaTarget[]>(`/projects/${projectId}/issues/sla`),

  updateSlaTargets: (projectId: string, input: UpdateSlaTargetsInput) =>
    apiFetch<IssueSlaTarget[]>(`/projects/${projectId}/issues/sla`, {
      method: 'PATCH',
      body: input,
    }),
};

export const issueKeys = {
  list: (projectId: string, query: IssueQuery) =>
    ['issues', projectId, query.state ?? 'all', query.openOnly ?? false] as const,
  detail: (issueId: string) => ['issues', 'detail', issueId] as const,
  comments: (issueId: string) => ['issues', 'comments', issueId] as const,
  assigneeCandidates: (issueId: string) => ['issues', 'assignees', issueId] as const,
  sla: (projectId: string) => ['issues', projectId, 'sla'] as const,
  clients: (projectId: string) => ['issues', projectId, 'clients'] as const,
  clientInvite: (projectId: string) => ['issues', projectId, 'client-invite'] as const,
  clientRequests: (projectId: string) => ['issues', projectId, 'client-requests'] as const,
  myClientRequests: ['me', 'client-requests'] as const,
  issueTypes: (projectId: string) => ['issues', projectId, 'types'] as const,
  flow: (projectId: string, range: { from?: string; to?: string }) =>
    ['issues', projectId, 'flow', range.from ?? '', range.to ?? ''] as const,
};
